export interface CameraInfo {
  id: string;
  name: string;
  internal_name: string;
  location: string;
  rtsp_url: string;
  backup_url?: string;
  status: 'idle' | 'recording' | 'stopping' | 'error';
  account_id?: string;
  account_name?: string;
  subdomain?: string;
}

export interface AccountWithCameras {
  account_id: string;
  account_name: string;
  subdomain?: string;
  cameras: CameraInfo[];
}

export interface RecordingRequest {
  camera_id: string;
  duration_seconds?: number;
  format: 'mp4' | 'jpeg';
  quality?: 'low' | 'medium' | 'high';
}

export interface RecordingResponse {
  id: string;
  camera_id: string;
  camera_name: string;
  filename: string;
  status: 'idle' | 'recording' | 'stopping' | 'error';
  started_at: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  file_path?: string;
}

export interface RecordingInfo {
  id: string;
  camera_id: string;
  camera_name: string;
  filename: string;
  started_at: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  status: 'idle' | 'recording' | 'stopping' | 'error';
}

// PlannedRecording interfaces
export interface PlannedRecording {
  _id: string;
  camera_id: string;
  camera_name: string;
  account_id: string;
  rtsp_url: string;
  recording_session_id?: string;
  
  // Timing
  scheduled_start_time: string;
  scheduled_end_time: string;
  actual_start_time?: string;
  actual_end_time?: string;
  duration_seconds: number;
  
  // Status tracking
  status: 'scheduled' | 'starting' | 'recording' | 'stopping' | 'completed' | 'failed' | 'cancelled';
  progress_percentage?: number;
  
  // File information
  filename?: string;
  file_path?: string;
  file_size_bytes?: number;
  recording_quality: 'low' | 'medium' | 'high';
  
  // Scheduling information
  schedule_type: 'manual' | 'auto_daily' | 'test';
  schedule_id?: string;
  clip_index?: number;
  
  // Error handling
  error_message?: string;
  retry_count: number;
  max_retries: number;
  
  // Metadata
  created_at: string;
  updated_at: string;
  
  // Logging
  log_entries: Array<{
    timestamp: string;
    level: 'info' | 'warn' | 'error';
    message: string;
    data?: any;
  }>;
}

export interface RecordingStats {
  total_recordings: number;
  active_recordings: number;
  by_status: Array<{
    _id: string;
    count: number;
    total_duration: number;
  }>;
  active_details: Array<{
    id: string;
    camera_name: string;
    status: string;
    progress: number;
    scheduled_start: string;
    actual_start?: string;
  }>;
}

class RecordingApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_RECORDING_API_URL || 'http://localhost:8000';
  }

  async getCameras(): Promise<CameraInfo[]> {
    const response = await fetch(`${this.baseUrl}/cameras`);
    if (!response.ok) {
      throw new Error(`Failed to fetch cameras: ${response.statusText}`);
    }
    return response.json();
  }

  async getCamerasGroupedByAccount(): Promise<AccountWithCameras[]> {
    const cameras = await this.getCameras();
    
    const accountMap = new Map<string, AccountWithCameras>();
    
    cameras.forEach(camera => {
      const accountId = camera.account_id || 'unknown';
      const accountName = camera.account_name || camera.location || 'Unknown Account';
      
      if (!accountMap.has(accountId)) {
        accountMap.set(accountId, {
          account_id: accountId,
          account_name: accountName,
          subdomain: camera.subdomain,
          cameras: []
        });
      }
      
      accountMap.get(accountId)!.cameras.push(camera);
    });
    
    return Array.from(accountMap.values()).sort((a, b) => 
      a.account_name.localeCompare(b.account_name)
    );
  }

  async getCamera(cameraId: string): Promise<CameraInfo> {
    const response = await fetch(`${this.baseUrl}/cameras/${cameraId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch camera: ${response.statusText}`);
    }
    return response.json();
  }

  async startRecording(request: RecordingRequest): Promise<RecordingResponse> {
    const response = await fetch(`${this.baseUrl}/recordings/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to start recording');
    }
    
    return response.json();
  }

  async stopRecording(recordingId: string): Promise<{ message: string; recording_id: string }> {
    const response = await fetch(`${this.baseUrl}/recordings/${recordingId}/stop`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to stop recording');
    }
    
    return response.json();
  }

  async getRecordings(): Promise<RecordingInfo[]> {
    const response = await fetch(`${this.baseUrl}/recordings`);
    if (!response.ok) {
      throw new Error(`Failed to fetch recordings: ${response.statusText}`);
    }
    return response.json();
  }

  async downloadRecording(recordingId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/recordings/${recordingId}/download`);
    if (!response.ok) {
      throw new Error(`Failed to download recording: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recording_${recordingId}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async takeScreenshot(cameraId: string): Promise<RecordingResponse> {
    const response = await fetch(`${this.baseUrl}/cameras/${cameraId}/screenshot`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to take screenshot');
    }
    
    return response.json();
  }

  async startQuickRecording(cameraId: string, durationSeconds: number = 30): Promise<RecordingResponse> {
    const response = await fetch(`${this.baseUrl}/cameras/${cameraId}/record?duration_seconds=${durationSeconds}`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || 'Failed to start quick recording');
    }
    
    return response.json();
  }
  
  // PlannedRecording API methods
  async getActivePlannedRecordings(account_id?: string): Promise<PlannedRecording[]> {
    console.log('🎬 Getting active planned recordings...');
    
    try {
      const params = account_id ? `?account_id=${account_id}` : '';
      const response = await fetch(`${this.baseUrl}/api/planned-recordings${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Active planned recordings fetched:', result.data?.length || 0);
      
      return result.data || [];
      
    } catch (error) {
      console.error('❌ Failed to fetch active planned recordings:', error);
      throw error;
    }
  }

  async getRecordingStats(account_id?: string): Promise<RecordingStats> {
    console.log('📊 Getting recording statistics...');
    
    try {
      const params = account_id ? `?account_id=${account_id}` : '';
      const response = await fetch(`${this.baseUrl}/api/planned-recordings/stats${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Recording statistics fetched');
      
      return result.data;
      
    } catch (error) {
      console.error('❌ Failed to fetch recording statistics:', error);
      throw error;
    }
  }
}

export const recordingApi = new RecordingApi(); 